/**
 * Form-state do editor de Playbook (Brief 4 · Tela 1 · PR4 · edição).
 *
 * Mantém um rascunho local dos campos EDITÁVEIS (os que o serializer suporta —
 * zero dado falso), com dirty-tracking por campo. `buildPatch()` devolve apenas
 * o que mudou em relação ao playbook carregado (ou `null` se nada mudou), no
 * shape que o `PATCH /v1/playbooks/:id` aceita: `steps`/`quickReplies` como
 * ARRAY PURO (R15 ao vivo confirmou que o backend grava array; a leitura tolera
 * `{ list }` legado). O baseline é reanexado quando o playbook recarrega
 * (id/version mudam) — pós-save ou pós-refetch de conflito.
 */

import {
  DEFAULT_VERIFICATION_MODE,
  DEFAULT_VERIFICATION_WAIT_HOURS,
  PROTECTION_TRIGGER_CATALOG,
  VERIFICATION_CONDITION_CATALOG,
  resolveEffectiveTriggers,
  resolvePlaybookVerification,
} from '@lume/shared';
import { useEffect, useMemo, useRef, useState } from 'react';

import { readList } from './playbook-labels';

import type {
  NativeSchedulingMode,
  PlaybookAutonomyPhase,
  PlaybookDto,
  PlaybookProactiveAppetite,
  PlaybookVerificationConfig,
  PlaybookOperationMode,
  PlaybookQuickReply,
  PlaybookStep,
  PlaybookTriggersConfig,
  PlaybookValidationMode,
  PlaybookVertical,
  UpdatePlaybookBody,
  WaitlistPromotionMode,
} from './types';
import type {
  ConductConfirmationStyle,
  ConductEscalationPosture,
  ConductGuidanceStyle,
  ConductRegisterPolicy,
  ConductResponseLength,
} from '../conduct/conduct-types';
import type { ProtectionTriggerId, VerificationMode, VerificationWaitHours } from '@lume/shared';

/** Gatilhos configuráveis pelo Gestor (catálogo ADR-148 menos o piso de plataforma). */
const CONFIGURABLE_TRIGGERS = PROTECTION_TRIGGER_CATALOG.filter((t) => !t.platformFloor);

/**
 * Rascunho da aba Gatilhos (ADR-148). `enabled` cobre os 8 configuráveis no
 * nível DO PLAYBOOK (piso e mínimos do tenant nunca entram no PATCH — são
 * travados na UI e ignorados pelo runtime de qualquer forma). Params como
 * string para input controlado; `''` = sem valor explícito (runtime cai em
 * env → 30min / default 3).
 */
export interface TriggersDraft {
  enabled: Record<string, boolean>;
  timeoutMinutes: string;
  maxConsecutiveRejections: string;
}

/** Snapshot da config de gatilhos no nível do playbook (sem mínimos do tenant). */
export function toTriggersDraft(playbook: PlaybookDto): TriggersDraft {
  const effective = resolveEffectiveTriggers({
    playbookTriggers: playbook.triggers,
    tenantMinimums: null,
  });
  const enabled: Record<string, boolean> = {};
  for (const def of CONFIGURABLE_TRIGGERS) {
    enabled[def.id] = effective[def.id as ProtectionTriggerId].enabled;
  }
  return {
    enabled,
    timeoutMinutes: effective['delegation-timeout'].params.timeoutMinutes?.toString() ?? '',
    maxConsecutiveRejections:
      effective['validation-rejected'].params.maxConsecutiveRejections?.toString() ?? '',
  };
}

function parseTriggerParam(raw: string, min: number, max: number): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  if (!Number.isInteger(n) || n < min || n > max) return null;
  return n;
}

/**
 * Reconstrói a config MÍNIMA canônica a partir do rascunho: só entram ids cujo
 * estado difere do default do catálogo, ou com param explícito. Tudo no
 * default ⇒ `null` (semântica canônica "defaults do catálogo"). Construção em
 * ordem do catálogo → comparável por JSON (dirty-tracking estável).
 */
export function buildTriggersConfig(draft: TriggersDraft): PlaybookTriggersConfig | null {
  const hard: NonNullable<PlaybookTriggersConfig['hard']> = {};
  const transition: NonNullable<PlaybookTriggersConfig['transition']> = {};
  for (const def of CONFIGURABLE_TRIGGERS) {
    const enabled = draft.enabled[def.id] ?? def.defaultEnabled;
    const differs = enabled !== def.defaultEnabled;
    if (def.kind === 'virada') {
      if (differs) transition[def.id] = { enabled };
      continue;
    }
    const params: { timeoutMinutes?: number; maxConsecutiveRejections?: number } = {};
    if (def.id === 'delegation-timeout') {
      const v = parseTriggerParam(draft.timeoutMinutes, 1, 1440);
      if (v !== null) params.timeoutMinutes = v;
    }
    if (def.id === 'validation-rejected') {
      const v = parseTriggerParam(draft.maxConsecutiveRejections, 1, 10);
      if (v !== null) params.maxConsecutiveRejections = v;
    }
    const hasParams = Object.keys(params).length > 0;
    if (differs || hasParams) {
      hard[def.id] = { enabled, ...(hasParams && { params }) };
    }
  }
  const hasHard = Object.keys(hard).length > 0;
  const hasTransition = Object.keys(transition).length > 0;
  if (!hasHard && !hasTransition) return null;
  return { version: 1, ...(hasHard && { hard }), ...(hasTransition && { transition }) };
}

/**
 * Rascunho da aba Verificação (ADR-149). Espelha o config resolvido pela MESMA
 * `resolvePlaybookVerification` do runtime — toggles em estado resolvido (com
 * defaults do catálogo aplicados); `templateId` `''` = nenhum selecionado.
 */
export interface VerificationDraft {
  enabled: boolean;
  waitHours: VerificationWaitHours;
  conditions: Record<string, boolean>;
  templateId: string;
  mode: VerificationMode;
}

/** Snapshot do config de verificação do playbook (null = desligada com defaults). */
export function toVerificationDraft(playbook: PlaybookDto): VerificationDraft {
  const effective = resolvePlaybookVerification(playbook.verification);
  const conditions: Record<string, boolean> = {};
  for (const def of VERIFICATION_CONDITION_CATALOG) {
    conditions[def.id] = effective?.conditions[def.id] ?? def.defaultEnabled;
  }
  return {
    enabled: effective !== null,
    waitHours: effective?.waitHours ?? DEFAULT_VERIFICATION_WAIT_HOURS,
    conditions,
    templateId: effective?.templateId ?? '',
    mode: effective?.mode ?? DEFAULT_VERIFICATION_MODE,
  };
}

/**
 * Reconstrói a config CANÔNICA a partir do rascunho: desligada ⇒ `null` (reset
 * — semântica "comportamento atual"); ligada ⇒ objeto completo em ordem fixa
 * com `conditions` só nas divergências do default do catálogo (comparável por
 * JSON — dirty-tracking estável, molde buildTriggersConfig).
 */
export function buildVerificationConfig(
  draft: VerificationDraft,
): PlaybookVerificationConfig | null {
  if (!draft.enabled) return null;
  const conditions: Record<string, boolean> = {};
  for (const def of VERIFICATION_CONDITION_CATALOG) {
    const value = draft.conditions[def.id] ?? def.defaultEnabled;
    if (value !== def.defaultEnabled) conditions[def.id] = value;
  }
  return {
    version: 1,
    enabled: true,
    waitHours: draft.waitHours,
    ...(Object.keys(conditions).length > 0 && { conditions }),
    templateId: draft.templateId === '' ? null : draft.templateId,
    mode: draft.mode,
  };
}

/** Os 5 campos de conduta, como chaves compartilhadas entre draft, DTO e patch. */
const CONDUCT_DRAFT_KEYS = [
  'conductRegisterPolicy',
  'conductGuidanceStyle',
  'conductResponseLength',
  'conductConfirmationStyle',
  'conductEscalationPosture',
] as const;

export interface PlaybookDraft {
  name: string;
  goal: string;
  vertical: PlaybookVertical;
  successCriteria: string;
  ethicalBoundaries: { redirectTopics: string[]; toneLimits: string[] };
  steps: PlaybookStep[];
  defaultOperationMode: PlaybookOperationMode;
  /** Item 368 F2 — Fase de autonomia da IA. */
  autonomyPhase: PlaybookAutonomyPhase;
  /** Frente B Plano 3 (B2) — override de agenda nativa; `null` = herda o Tenant. */
  nativeSchedulingMode: NativeSchedulingMode | null;
  /** Frente B Plano 3 (B2) — override do modo de promoção da fila; `null` = herda o Tenant. */
  waitlistPromotionMode: WaitlistPromotionMode | null;
  validationMode: PlaybookValidationMode;
  proactiveAppetite: PlaybookProactiveAppetite;
  quickReplies: PlaybookQuickReply[];
  autoCloseable: boolean;
  autoCloseableOutcomes: string[];
  checklistTemplateId: string | null;
  /**
   * Fase 0b (auditoria 05/Ago) — Persona que dá a VOZ à IA neste playbook. É o
   * único caminho pelo qual a persona chega ao motor (`ai-conductor.service.ts:2925-2929`);
   * não existe persona de tenant. `null` = a IA conduz sem voz configurada.
   */
  personaId: string | null;
  /**
   * Fase 2 «Conduta e Contexto» — os 5 eixos neste nível. `null` = «não opinei, herda o
   * nível acima». É rascunho como qualquer outro campo: o `buildPatch` compara com o
   * servidor e ENVIA o `null` quando o usuário solta a opinião.
   */
  conductRegisterPolicy: ConductRegisterPolicy | null;
  conductGuidanceStyle: ConductGuidanceStyle | null;
  conductResponseLength: ConductResponseLength | null;
  conductConfirmationStyle: ConductConfirmationStyle | null;
  conductEscalationPosture: ConductEscalationPosture | null;
  /** ADR-148 — aba Gatilhos. */
  triggers: TriggersDraft;
  /** ADR-149 — aba Verificação. */
  verification: VerificationDraft;
}

/** Snapshot dos campos editáveis a partir do DTO carregado (normaliza wire-shape). */
export function toDraft(playbook: PlaybookDto): PlaybookDraft {
  return {
    name: playbook.name,
    goal: playbook.goal,
    vertical: playbook.vertical,
    successCriteria: playbook.successCriteria,
    ethicalBoundaries: {
      redirectTopics: [...playbook.ethicalBoundaries.redirectTopics],
      toneLimits: [...playbook.ethicalBoundaries.toneLimits],
    },
    steps: readList<PlaybookStep>(playbook.steps).map((s) => ({ ...s })),
    defaultOperationMode: playbook.defaultOperationMode,
    autonomyPhase: playbook.autonomyPhase,
    nativeSchedulingMode: playbook.nativeSchedulingMode,
    conductRegisterPolicy: playbook.conductRegisterPolicy,
    conductGuidanceStyle: playbook.conductGuidanceStyle,
    conductResponseLength: playbook.conductResponseLength,
    conductConfirmationStyle: playbook.conductConfirmationStyle,
    conductEscalationPosture: playbook.conductEscalationPosture,
    waitlistPromotionMode: playbook.waitlistPromotionMode,
    validationMode: playbook.validationMode,
    proactiveAppetite: playbook.proactiveAppetite,
    quickReplies: readList<PlaybookQuickReply>(playbook.quickReplies).map((q) => ({ ...q })),
    autoCloseable: playbook.autoCloseable,
    autoCloseableOutcomes: [...playbook.autoCloseableOutcomes],
    checklistTemplateId: playbook.checklistTemplateId,
    personaId: playbook.personaId,
    triggers: toTriggersDraft(playbook),
    verification: toVerificationDraft(playbook),
  };
}

/** Cria uma etapa nova vazia (ações como array de strings — shape comum/editável). */
export function makeStep(id: string): PlaybookStep {
  return { id, name: '', objective: '', actions: [], successCriteria: '' };
}

/** Cria uma quick reply nova vazia. */
export function makeQuickReply(id: string): PlaybookQuickReply {
  return { id, label: '', text: '', shortcut: '' };
}

function jsonEq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Diff entre o playbook carregado e o rascunho. Inclui um campo só se mudou.
 * Retorna `null` quando nada mudou (base do dirty-tracking). Strings são
 * trimadas em campos curtos; quick replies normalizam `shortcut` vazio para
 * ausência (não enviar string vazia que o backend interpretaria como atalho).
 */
export function buildPatch(playbook: PlaybookDto, draft: PlaybookDraft): UpdatePlaybookBody | null {
  const patch: UpdatePlaybookBody = {};

  if (draft.name !== playbook.name) patch.name = draft.name;
  if (draft.goal !== playbook.goal) patch.goal = draft.goal;
  if (draft.vertical !== playbook.vertical) patch.vertical = draft.vertical;
  if (draft.successCriteria !== playbook.successCriteria) {
    patch.successCriteria = draft.successCriteria;
  }
  // Compara os arrays individualmente — o serializer pode entregar as chaves
  // (`redirectTopics`/`toneLimits`) em ordem diferente do snapshot, e jsonEq é
  // sensível à ordem das chaves (senão a tela abre "suja" sem nenhuma edição).
  if (
    !jsonEq(draft.ethicalBoundaries.redirectTopics, playbook.ethicalBoundaries.redirectTopics) ||
    !jsonEq(draft.ethicalBoundaries.toneLimits, playbook.ethicalBoundaries.toneLimits)
  ) {
    patch.ethicalBoundaries = draft.ethicalBoundaries;
  }

  const baselineSteps = readList<PlaybookStep>(playbook.steps);
  if (!jsonEq(draft.steps, baselineSteps)) patch.steps = draft.steps;

  if (draft.defaultOperationMode !== playbook.defaultOperationMode) {
    patch.defaultOperationMode = draft.defaultOperationMode;
  }
  if (draft.autonomyPhase !== playbook.autonomyPhase) {
    patch.autonomyPhase = draft.autonomyPhase;
  }
  // Frente B Plano 3 (B2) — overrides de agenda por-playbook (null = herda Tenant).
  // Fase 2 — os 5 eixos de conduta. `null` é enviado quando mudou: é o «soltar».
  for (const key of CONDUCT_DRAFT_KEYS) {
    if (draft[key] !== playbook[key]) {
      // Cast: TS não correlaciona a chave do laço com o valor por união.
      (patch as Record<string, unknown>)[key] = draft[key];
    }
  }

  if (draft.nativeSchedulingMode !== playbook.nativeSchedulingMode) {
    patch.nativeSchedulingMode = draft.nativeSchedulingMode;
  }
  if (draft.waitlistPromotionMode !== playbook.waitlistPromotionMode) {
    patch.waitlistPromotionMode = draft.waitlistPromotionMode;
  }
  if (draft.validationMode !== playbook.validationMode) {
    patch.validationMode = draft.validationMode;
  }
  if (draft.proactiveAppetite !== playbook.proactiveAppetite) {
    patch.proactiveAppetite = draft.proactiveAppetite;
  }

  const baselineQr = readList<PlaybookQuickReply>(playbook.quickReplies);
  const normalizedQr = draft.quickReplies.map((q) => {
    const next: PlaybookQuickReply = { id: q.id, label: q.label, text: q.text };
    if (q.shortcut !== undefined && q.shortcut !== '') next.shortcut = q.shortcut;
    return next;
  });
  const normalizedBaselineQr = baselineQr.map((q) => {
    const next: PlaybookQuickReply = { id: q.id, label: q.label, text: q.text };
    if (q.shortcut !== undefined && q.shortcut !== '') next.shortcut = q.shortcut;
    return next;
  });
  if (!jsonEq(normalizedQr, normalizedBaselineQr)) patch.quickReplies = normalizedQr;

  if (draft.autoCloseable !== playbook.autoCloseable) patch.autoCloseable = draft.autoCloseable;
  if (!jsonEq(draft.autoCloseableOutcomes, playbook.autoCloseableOutcomes)) {
    patch.autoCloseableOutcomes = draft.autoCloseableOutcomes;
  }
  if (draft.checklistTemplateId !== playbook.checklistTemplateId) {
    patch.checklistTemplateId = draft.checklistTemplateId;
  }
  // Fase 0b — só DEFINIR. `personaId` no corpo é `string?` SEM `| null` (o DTO do
  // Engine valida `@IsString`), então o patch não consegue expressar «nenhuma» —
  // por isso a opção vazia do seletor é `disabled`. Desligar uma persona já ligada
  // exige mudança de contrato da API (fora do escopo desta fatia).
  if (draft.personaId !== null && draft.personaId !== playbook.personaId) {
    patch.personaId = draft.personaId;
  }

  // ADR-148 — compara as configs CANÔNICAS reconstruídas (baseline e rascunho
  // saem da mesma função, em ordem do catálogo → jsonEq estável; config crua
  // legada com entradas redundantes não abre a tela "suja").
  const baselineTriggers = buildTriggersConfig(toTriggersDraft(playbook));
  const draftTriggers = buildTriggersConfig(draft.triggers);
  if (!jsonEq(baselineTriggers, draftTriggers)) patch.triggers = draftTriggers;

  // ADR-149 — mesma estratégia canônica×canônica da aba Gatilhos.
  const baselineVerification = buildVerificationConfig(toVerificationDraft(playbook));
  const draftVerification = buildVerificationConfig(draft.verification);
  if (!jsonEq(baselineVerification, draftVerification)) patch.verification = draftVerification;

  return Object.keys(patch).length > 0 ? patch : null;
}

export interface PlaybookForm {
  draft: PlaybookDraft;
  set: <K extends keyof PlaybookDraft>(key: K, value: PlaybookDraft[K]) => void;
  isDirty: boolean;
  reset: () => void;
  buildPatch: () => UpdatePlaybookBody | null;
}

export function usePlaybookForm(playbook: PlaybookDto): PlaybookForm {
  const baselineRef = useRef(playbook);
  const [draft, setDraft] = useState<PlaybookDraft>(() => toDraft(playbook));

  // Reanexa o baseline e descarta o rascunho quando o playbook recarrega
  // (pós-save → version++ ; pós-refetch de conflito → version do servidor).
  useEffect(() => {
    baselineRef.current = playbook;
    setDraft(toDraft(playbook));
  }, [playbook.id, playbook.version]);

  const set = <K extends keyof PlaybookDraft>(key: K, value: PlaybookDraft[K]): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const patch = useMemo(() => buildPatch(baselineRef.current, draft), [draft]);

  return {
    draft,
    set,
    isDirty: patch !== null,
    reset: () => setDraft(toDraft(baselineRef.current)),
    buildPatch: () => buildPatch(baselineRef.current, draft),
  };
}
